/**
 * Honest public coupon signals (SEO no-regret fixes, 2026-10).
 *
 * The page used to show schema defaults as if they were data: "100% Success"
 * (successRate defaults to 100), "Used 0 times today" (usedToday is a lifetime
 * counter that is never reset), today's date as "Updated", and invented
 * `validThrough` dates in JSON-LD. These helpers decide what is real enough to
 * show. They are pure so they can be unit-tested.
 */

type DateLike = Date | string | null | undefined;

const MONTHS = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
];

function toDate(value: DateLike): Date | null {
    if (value === null || value === undefined || value === "") return null;
    const d = value instanceof Date ? value : new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * The stored successRate is only derived from community votes for coupons
 * that are NOT verified and have at least one VALID vote (see /api/vote and
 * voteCoupon). Everything else is the column default (100) or a value forced
 * by verification, so it is not a measured success rate and must not be shown.
 *
 * `voteCount` MUST be the number of valid votes (lib/valid-votes: bots, repeat
 * votes and votes older than 180 days out), the same votes the rate is made
 * from: `_count.votes` counts all of them and let a coupon with only bot votes
 * show "100%" (audit 2026-10-11, B1). Use `validVoteCountOf` / `validVoteCounts`.
 */
export function realSuccessRate(coupon: {
    verified?: boolean | null;
    successRate?: number | null;
    voteCount?: number | null;
}): number | undefined {
    if (coupon.verified) return undefined;
    if (!coupon.voteCount || coupon.voteCount < 1) return undefined;
    if (typeof coupon.successRate !== "number" || !Number.isFinite(coupon.successRate)) return undefined;
    return Math.min(100, Math.max(0, Math.round(coupon.successRate)));
}

/** Fewest real (valid) votes behind a displayed success percentage (SEO R3 rule 5, 2026-10-24). */
export const SUCCESS_RATE_MIN_VOTES = 3;

/**
 * The success percentage a store page shows (SEO R3 rule 5): the real ratio of valid votes, worked / (worked + failed),
 * and only with at least SUCCESS_RATE_MIN_VOTES of them. No default, no floor, no formula: 2 up and 1 down is 67 %.
 * Takes the valid shopper votes of a coupon history (lib/valid-votes: bots, repeat votes and old votes out).
 */
export function voteRatioSuccessRate(history: { shopperWorked?: number | null; shopperFailed?: number | null } | null | undefined): number | undefined {
    const up = Math.max(0, history?.shopperWorked ?? 0);
    const down = Math.max(0, history?.shopperFailed ?? 0);
    if (up + down < SUCCESS_RATE_MIN_VOTES) return undefined;
    return Math.round((100 * up) / (up + down));
}

/** Valid shopper votes (up + down) of a coupon history; 0 without one. */
export function validVoteCountOf(history: { shopperWorked?: number | null; shopperFailed?: number | null } | null | undefined): number {
    return Math.max(0, history?.shopperWorked ?? 0) + Math.max(0, history?.shopperFailed ?? 0);
}

/** Average of the real success rates only; null when no coupon has one. */
export function averageRealSuccessRate(rates: Array<number | undefined | null>): number | null {
    const real = rates.filter((r): r is number => typeof r === "number");
    if (real.length === 0) return null;
    return real.reduce((a, b) => a + b, 0) / real.length;
}

/**
 * `usedToday` is incremented on reveals and votes and never reset, so it is an
 * all-time count. Label it as such, and show nothing when it is zero.
 */
export function usageLabel(count: number | null | undefined): string | null {
    if (typeof count !== "number" || !Number.isFinite(count) || count < 1) return null;
    const n = Math.floor(count);
    return `Used ${n.toLocaleString("en-US")} ${n === 1 ? "time" : "times"}`;
}

/**
 * The last real change on a store page: the newest coupon added or the newest
 * time a coupon was checked. `updatedAt` is deliberately NOT used: it is bumped
 * by every reveal/vote counter increment, so it would just mean "today".
 * Only coupons visible on the page (ACTIVE / EXPIRED) count. Falls back to the
 * store's own updatedAt when no coupon has a date.
 */
export function lastChangedAt(
    coupons: Array<{ status?: string | null; createdAt?: DateLike; lastVerifiedAt?: DateLike }>,
    fallback?: DateLike,
): Date | null {
    let latest: Date | null = null;
    for (const c of coupons) {
        if (c.status && c.status !== "ACTIVE" && c.status !== "EXPIRED") continue;
        for (const value of [c.createdAt, c.lastVerifiedAt]) {
            const d = toDate(value);
            if (d && (!latest || d > latest)) latest = d;
        }
    }
    return latest ?? toDate(fallback);
}

/** "05 October 2026" (UTC, locale-independent, same shape the page used before). */
export function formatDayMonthYear(value: DateLike): string | null {
    const d = toDate(value);
    if (!d) return null;
    return `${String(d.getUTCDate()).padStart(2, "0")} ${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}

/** "October 2026" for the given date (default: now, UTC). */
export function formatMonthYear(value: DateLike = new Date()): string {
    const d = toDate(value) ?? new Date();
    return `${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}

/**
 * Coupon.expiry is free text. Return an ISO date (YYYY-MM-DD) only when it is a
 * real, parseable date; otherwise null so JSON-LD omits validThrough instead of
 * inventing one.
 */
export function isoDateOrNull(value: DateLike): string | null {
    const d = toDate(value);
    if (!d) return null;
    const year = d.getUTCFullYear();
    if (year < 2000 || year > 2100) return null;
    return d.toISOString().split("T")[0];
}

/** Number of coupons that are both live and verified: the only basis for a "Verified" claim. */
export function activeVerifiedCount(coupons: Array<{ status?: string | null; verified?: boolean | null }>): number {
    return coupons.filter((c) => c.status === "ACTIVE" && c.verified === true).length;
}
